import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  AvailableFilters,
  InvoicesDashboard,
} from '@/app/features/DebtorPortal/Dashboard/dashboard.types';
import {
  useSearchInvoices,
  getSearchInvoicesCSV,
} from '@/app/shared/api/endpoints/SearchInvoices';
import { UseInvoicesTableOptions } from '../utils/invoiceUtils/invoiceTypes';
import { transformInvoicesForDisplay } from '../utils/invoiceUtils/invoiceTransformers';
import { useInvoicesPagination } from '../utils/hooks/invoiceHooks/useInvoicePagination';
import { useInvoicesSorting } from '../utils/hooks/invoiceHooks/useInvoiceSorting';
import { useInvoicesFilters } from '../utils/hooks/invoiceHooks/useInvoiceFilters';
import { useInvoicesSelection } from '../utils/hooks/invoiceHooks/useInvoiceSelection';
import { useInvoicesPendingChanges } from '../utils/hooks/invoiceHooks/useInvoicePendingChanges';
import { FileType } from '../constants/misc/FileType';

export const useInvoicesTable = (options: UseInvoicesTableOptions = {}) => {
  const { enabled = true } = options;

  // Raw table data from API
  const [rawRows, setRawRows] = useState<InvoicesDashboard[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters>();

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Initialize pagination hook
  const pagination = useInvoicesPagination();

  // Initialize sorting hook with reset to first page callback
  const sorting = useInvoicesSorting(pagination.resetToFirstPage);

  // Initialize filters hook with reset to first page callback
  const filters = useInvoicesFilters(pagination.resetToFirstPage);

  // Initialize pending changes hook
  const {
    rows,
    pendingChanges,
    modifiedRows,
    hasChangesToSubmit,
    invoiceIdsWithChanges,
    handlePaymentDateChange,
    handleWillNotPayChange,
    updateRowWithModalData,
    resetRowChanges,
    resetAllChanges,
    rowHasChanges,
    handleSubmitUpdates: submitUpdates,
    handleBulkPaymentDateChangeAndSubmit: bulkPaymentDateChangeAndSubmit,
    handleBulkWillNotPayChangeAndSubmit: bulkWillNotPayChangeAndSubmit,
    isSubmitting,
    submitError,
    isFormValid,
    hasRowsWithWarnings,
    rowsWithWarningsCount,
    invoiceIdsWithWarnings,
    rowHasWarning,
    isRowPastDue,
    hasInvalidModifiedRows,
    invalidModifiedRowsCount,
  } = useInvoicesPendingChanges(rawRows);

  // Initialize selection hook with merged rows
  const selection = useInvoicesSelection(rows);

  // Build the search request
  const searchRequest = useMemo(() => {
    return {
      pageSize: pagination.pageSize,
      page: pagination.currentPage,
      isCsv: false,
      sort: sorting.sortConfig,
      filter: filters.combinedFilter,
      paymentsNotReceived: filters.isPaymentsNotReceived,
      needsAttention: filters.needsAttention,
    };
  }, [
    pagination.pageSize,
    pagination.currentPage,
    sorting.sortConfig,
    filters.combinedFilter,
    filters.isPaymentsNotReceived,
    filters.needsAttention,
  ]);

  // Fetch invoice data
  const {
    data: invoiceData,
    isLoading,
    refetch,
  } = useSearchInvoices(searchRequest, {
    enabled,
  });

  // Wrap submit to include refetch
  const handleSubmitUpdates = useCallback(async (): Promise<boolean> => {
    const success = await submitUpdates();
    if (success) {
      await refetch();
    }
    return success;
  }, [submitUpdates, refetch]);

  // Wrap bulk payment date change and submit to include refetch
  const handleBulkPaymentDateChangeAndSubmit = useCallback(
    async (
      invoiceNumbers: string[],
      selectedPaymentDate: string,
      note: string = '',
    ): Promise<boolean> => {
      const success = await bulkPaymentDateChangeAndSubmit(
        invoiceNumbers,
        selectedPaymentDate,
        note,
      );
      if (success) {
        await refetch();
      }
      return success;
    },
    [bulkPaymentDateChangeAndSubmit, refetch],
  );

  // Wrap bulk will not pay change and submit to include refetch
  const handleBulkWillNotPayChangeAndSubmit = useCallback(
    async (
      invoiceNumbers: string[],
      disputedReasonId: string,
      disputedReasonDesc: string,
      note: string,
    ): Promise<boolean> => {
      const success = await bulkWillNotPayChangeAndSubmit(
        invoiceNumbers,
        disputedReasonId,
        disputedReasonDesc,
        note,
      );
      if (success) {
        await refetch();
      }
      return success;
    },
    [bulkWillNotPayChangeAndSubmit, refetch],
  );

  // Process invoice data when it changes
  useEffect(() => {
    if (invoiceData) {
      const cleanedData = transformInvoicesForDisplay(invoiceData.results);
      setRawRows(cleanedData);
      setTotalRows(invoiceData.totalResults ?? 0);
      setAvailableFilters(invoiceData.availableFilters);
    }
  }, [invoiceData]);

  // Get row by invoice number
  const getRowByInvoiceNumber = useCallback(
    (invoiceNumber: string): InvoicesDashboard | undefined => {
      return rows.find((row) => row.invoiceNumber === invoiceNumber);
    },
    [rows],
  );

  // Get selected invoice data
  const getSelectedInvoiceData = useCallback((): InvoicesDashboard[] => {
    return selection.getSelectedInvoiceData(rows);
  }, [selection, rows]);

  // Export handler
  const handleExportList = useCallback(
    async (fileType: FileType) => {
      if (!enabled) return;

      setIsExporting(true);
      try {
        await getSearchInvoicesCSV(
          filters.combinedFilter,
          sorting.sortConfig,
          filters.isPaymentsNotReceived,
          filters.needsAttention,
          fileType,
        );
      } catch (error) {
        console.error('Failed to export invoices:', error);
      } finally {
        setIsExporting(false);
      }
    },
    [
      enabled,
      filters.combinedFilter,
      sorting.sortConfig,
      filters.isPaymentsNotReceived,
      filters.needsAttention,
    ],
  );

  // Bulk payment date change handler (without submit - for compatibility)
  const handleBulkPaymentDateChange = useCallback(
    (invoiceNumbers: string[], selectedPaymentDate: string) => {
      invoiceNumbers.forEach((invoiceNumber) => {
        handlePaymentDateChange(invoiceNumber, selectedPaymentDate);
      });
    },
    [handlePaymentDateChange],
  );

  // Bulk will not pay change handler (without submit - for compatibility)
  const handleBulkWillNotPayChange = useCallback(
    (
      invoiceNumbers: string[],
      disputedReasonId: string,
      disputedReasonDesc: string,
      note: string,
    ) => {
      invoiceNumbers.forEach((invoiceNumber) => {
        handleWillNotPayChange(
          invoiceNumber,
          disputedReasonId,
          disputedReasonDesc,
          note,
        );
      });
    },
    [handleWillNotPayChange],
  );

  // Return backward-compatible API
  return {
    // Data
    rows,
    rawRows,
    totalRows,
    availableFilters,
    isLoading: enabled ? isLoading : true,

    // Pagination
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    handlePageChange: pagination.handlePageChange,
    handlePageSizeChange: pagination.handlePageSizeChange,

    // Sorting
    sortConfig: sorting.sortConfig,
    handleOnSort: sorting.handleOnSort,

    // Filtering
    searchTerm: filters.searchTerm,
    handleSearchChange: filters.handleSearchChange,
    selectedDateRangeIndex: filters.selectedDateRangeIndex,
    currentDateRange: filters.currentDateRange,
    handleDateRangeChange: filters.handleDateRangeChange,

    // Widget filters
    handlePaymentsNotReceivedChange: filters.handlePaymentsNotReceivedChange,
    handleUpcomingInvoicesChange: filters.handleUpcomingInvoicesChange,
    handlePaymentsPastDueChange: filters.handlePaymentsPastDueChange,

    //Needs Attention filter
    needsAttention: filters.needsAttention,
    handleNeedsAttentionChange: filters.handleNeedsAttentionChange,

    // Button filters
    buttonFilters: filters.buttonFilters,
    handleButtonFilterChange: filters.handleButtonFilterChange,
    getActiveFiltersForColumn: filters.getActiveFiltersForColumn,

    // Selection
    selectedInvoices: selection.selectedInvoices,
    handleSelectInvoice: selection.handleSelectInvoice,
    handleSelectAllOnPage: selection.handleSelectAllOnPage,
    handleClearAllSelections: selection.handleClearAllSelections,
    allCurrentPageSelected: selection.allCurrentPageSelected,
    someCurrentPageSelected: selection.someCurrentPageSelected,
    getSelectedInvoiceData,

    // Row updates
    handlePaymentDateChange,
    updateRowWithModalData,

    // Bulk updates (without immediate submit)
    handleBulkPaymentDateChange,
    handleBulkWillNotPayChange,

    // Bulk updates (with immediate submit)
    handleBulkPaymentDateChangeAndSubmit,
    handleBulkWillNotPayChangeAndSubmit,

    // Utilities
    getRowByInvoiceNumber,
    isFormValid,

    // Export
    handleExportList,
    isExporting,

    // Submit functionality
    pendingChanges,
    modifiedRows,
    hasChangesToSubmit,
    handleSubmitUpdates,
    isSubmitting,
    submitError,
    resetRowChanges,
    resetAllChanges,
    invoiceIdsWithChanges,
    rowHasChanges,

    // Validation - all rows (for display)
    hasRowsWithWarnings,
    rowsWithWarningsCount,
    invoiceIdsWithWarnings,
    rowHasWarning,
    isRowPastDue,

    // Validation - modified rows only (for submission)
    hasInvalidModifiedRows,
    invalidModifiedRowsCount,

    // Reset filters
    resetAllFilters: filters.resetAllFilters,
    hasActiveFilters: filters.hasActiveFilters,
    filterResetKey: filters.filterResetKey,
  };
};
